"use client";
import useIsClient from "@/hooks/useIsClient";
import { useEffect } from "react";

const ScriptInject: React.FC<{ id: string | number; script: string }> = ({
  script,
  id,
}) => {
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient || !script) return;

    const scriptId = `html-block-custom-script-${id}`;
    
    // Use a mutation observer to wait for the modal content to be available
    const observer = new MutationObserver((_mutations, obs) => {
      const modalContent = document.querySelector('.modal-content');
      if (modalContent) {
        // Clean up any existing script with the same ID to prevent duplicates
        const existingScript = document.getElementById(scriptId);
        if (existingScript) {
          existingScript.remove();
        }

        const scriptEl = document.createElement("script");
        scriptEl.id = scriptId;
        // Wrap script in a try-catch block and IIFE to isolate scope
        scriptEl.innerHTML = `
          try {
            (function() {
              ${script}
            })();
          } catch(e) {
            console.error('Script injection error:', e);
          }
        `;
        document.body.appendChild(scriptEl);
        obs.disconnect(); // Stop observing once script is injected
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Fallback in case observer doesn't trigger (e.g., content already there)
    const timeoutId = setTimeout(() => {
       const existingScript = document.getElementById(scriptId);
       if (!existingScript) { // Only if not already injected by observer
          const scriptEl = document.createElement("script");
          scriptEl.id = scriptId;
          scriptEl.innerHTML = `
            try {
              (function() {
                ${script}
              })();
            } catch(e) {
              console.error('Script injection error:', e);
            }
          `;
          document.body.appendChild(scriptEl);
       }
    }, 500);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [script, id, isClient]);

  return null;
};

export default ScriptInject;
